import React, { useState } from 'react';
import { formatAbsoluteDate, formatRelativeDate } from '../formatRelativeDate';
import { compareReplayDates } from '../replayDateSortKey';
import './ReplaysList.css';

const emptyAvailability = { sc2: new Set(), parsed: new Set(), ai: new Set() };

const ReplaysList = ({
  replays,
  assetAvailability = emptyAvailability,
  onReplaySelect,
  onRefresh,
  onTriggerUpdate,
  onUpload,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('replay_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100); // number | 'all'
  const [viewMode, setViewMode] = useState('list'); // list | grid
  // null = no filter; 'sc2'|'parsed'|'ai' = has asset; 'missing_*' = missing asset
  const [assetFilter, setAssetFilter] = useState(null);

  const toggleAssetFilter = (kind) => {
    setAssetFilter((prev) => {
      if (prev === kind) return `missing_${kind}`;
      if (prev === `missing_${kind}`) return null;
      return kind;
    });
  };

  const filteredReplays = replays
    .filter(replay => {
      const q = searchTerm.toLowerCase();
      const fingerprintText = (replay.player_fingerprints || [])
        .map((p) => [p.name, p.label, p.first_army, p.race].filter(Boolean).join(' '))
        .join(' ')
        .toLowerCase();
      return (
        (replay.map_name || '').toLowerCase().includes(q) ||
        (replay.winning_player || '').toLowerCase().includes(q) ||
        (replay.format || '').toLowerCase().includes(q) ||
        String(replay.replay_id || '').toLowerCase().includes(q) ||
        fingerprintText.includes(q)
      );
    })
    .filter(replay => {
      if (!assetFilter) return true;
      const id = replay.replay_id;
      if (assetFilter === 'sc2') return assetAvailability.sc2.has(id);
      if (assetFilter === 'parsed') return assetAvailability.parsed.has(id);
      if (assetFilter === 'ai') return assetAvailability.ai.has(id);
      if (assetFilter === 'missing_sc2') return !assetAvailability.sc2.has(id);
      if (assetFilter === 'missing_parsed') return !assetAvailability.parsed.has(id);
      if (assetFilter === 'missing_ai') return !assetAvailability.ai.has(id);
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'replay_date') {
        return compareReplayDates(a.replay_date, b.replay_date, sortOrder);
      }
      let comparison = 0;
      if (sortBy === 'map_name') {
        comparison = (a.map_name || '').localeCompare(b.map_name || '');
      } else if (sortBy === 'game_length') {
        comparison = (a.game_length || 0) - (b.game_length || 0);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  const showAll = pageSize === 'all';
  const pageSizeNum = showAll ? filteredReplays.length || 1 : pageSize;
  const totalPages = showAll ? 1 : Math.max(1, Math.ceil(filteredReplays.length / pageSizeNum));
  const startIndex = showAll ? 0 : (currentPage - 1) * pageSizeNum;
  const endIndex = showAll ? filteredReplays.length : startIndex + pageSizeNum;
  const paginatedReplays = filteredReplays.slice(startIndex, endIndex);

  // Reset to first page when search/filter/page size changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy, sortOrder, assetFilter, pageSize]);

  // Keep current page in range if the filtered set shrinks
  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = (placement) => {
    if (showAll || totalPages <= 1) return null;
    return (
      <div className={`pagination-controls pagination-${placement}`}>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="pagination-btn"
        >
          ← Previous
        </button>
        <div className="pagination-info">
          <span>Page {currentPage} of {totalPages}</span>
        </div>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="pagination-btn"
        >
          Next →
        </button>
      </div>
    );
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderFingerprints = (replay) => {
    const prints = replay.player_fingerprints || [];
    if (prints.length === 0) {
      if (assetAvailability.parsed.has(replay.replay_id)) {
        return (
          <span className="build-fingerprint empty" title="Force re-parse to compute Build Fingerprint">
            —
          </span>
        );
      }
      return <span className="build-fingerprint empty"> </span>;
    }
    return (
      <div className="build-fingerprints" data-testid={`fingerprints-${replay.replay_id}`}>
        {prints.map((p) => (
          <div
            key={`${replay.replay_id}-${p.name}`}
            className="build-fingerprint"
            title={p.emoji || p.label}
          >
            <span className="build-fingerprint-player">{p.name}</span>
            <span className="build-fingerprint-label">{p.label || '—'}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="replays-list">
      <div className="controls">
        <div className="search-controls">
          <input
            type="text"
            placeholder="Search replays..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button onClick={onRefresh} className="refresh-btn">
            Refresh
          </button>
          {onTriggerUpdate && (
            <button onClick={onTriggerUpdate} className="refresh-btn">
              Update from SC2 API
            </button>
          )}
          {onUpload && (
            <label className="refresh-btn" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
              Upload replay
              <input
                type="file"
                accept=".SC2Replay,application/octet-stream"
                data-testid="replay-upload-input"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  e.target.value = '';
                  if (file) onUpload(file);
                }}
              />
            </label>
          )}
        </div>
        
        <div className="sort-controls">
          <div className="view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`view-toggle-btn${viewMode === 'list' ? ' active' : ''}`}
              aria-pressed={viewMode === 'list'}
              title="List view"
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              type="button"
              className={`view-toggle-btn${viewMode === 'grid' ? ' active' : ''}`}
              aria-pressed={viewMode === 'grid'}
              title="Grid view"
              onClick={() => setViewMode('grid')}
            >
              Grid
            </button>
          </div>
          <label className="page-size-control">
            <span className="page-size-label">Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const value = e.target.value;
                setPageSize(value === 'all' ? 'all' : Number(value));
              }}
              className="sort-select"
              title="Items per page"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value="all">All</option>
            </select>
          </label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="replay_date">Date</option>
            <option value="map_name">Map</option>
            <option value="game_length">Duration</option>
          </select>
          <button 
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-order-btn"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      <div className="replays-count">
        {showAll
          ? `Showing all ${filteredReplays.length} replays`
          : `Showing ${paginatedReplays.length} of ${filteredReplays.length} replays (Page ${currentPage} of ${totalPages})`}
        <span className="asset-filters" role="group" aria-label="Filter by stored assets">
          {['sc2', 'parsed', 'ai'].map((kind) => {
            const label = kind === 'sc2' ? 'SC2' : kind === 'parsed' ? 'Parsed' : 'AI';
            const hasActive = assetFilter === kind;
            const missingActive = assetFilter === `missing_${kind}`;
            const mode = hasActive ? 'present' : missingActive ? 'missing' : 'idle';
            const title = hasActive
              ? `Showing replays with ${label} — click for missing`
              : missingActive
                ? `Showing replays without ${label} — click to clear`
                : `Filter: has ${label}`;
            return (
              <button
                key={kind}
                type="button"
                className={`asset-badge asset-filter-btn ${mode} ${kind}${hasActive || missingActive ? ' active' : ''}`}
                aria-pressed={hasActive || missingActive}
                title={title}
                onClick={() => toggleAssetFilter(kind)}
              >
                {label}
              </button>
            );
          })}
          <span className="asset-filter-hint">
            {assetFilter?.startsWith('missing_')
              ? '= missing'
              : assetFilter
                ? '= available'
                : '= filter'}
          </span>
        </span>
      </div>

      {renderPagination('top')}

      <div className={viewMode === 'list' ? 'replays-table' : 'replays-grid'}>
        {viewMode === 'list' && (
          <div className="replay-row replay-row-header" aria-hidden="true">
            <span>Map</span>
            <span>Assets</span>
            <span>Build</span>
            <span>Date</span>
            <span>Duration</span>
            <span>Format</span>
            <span>Winner</span>
            <span>Type</span>
            <span>ID</span>
          </div>
        )}
        {paginatedReplays.map(replay => {
          const id = replay.replay_id;
          const hasSc2 = assetAvailability.sc2.has(id);
          const hasParsed = assetAvailability.parsed.has(id);
          const hasAi = assetAvailability.ai.has(id);
          const assetBadges = (
            <div className="asset-badges" aria-label="Stored assets">
              <span
                className={`asset-badge ${hasSc2 ? 'present' : 'missing'} sc2`}
                title={hasSc2 ? 'SC2Replay file stored' : 'SC2Replay file missing'}
              >
                SC2
              </span>
              <span
                className={`asset-badge ${hasParsed ? 'present' : 'missing'} parsed`}
                title={hasParsed ? 'Parsed game available' : 'Not parsed yet'}
              >
                Parsed
              </span>
              <span
                className={`asset-badge ${hasAi ? 'present' : 'missing'} ai`}
                title={hasAi ? 'AI analysis available' : 'No AI analysis'}
              >
                AI
              </span>
            </div>
          );

          if (viewMode === 'list') {
            return (
              <div
                key={id}
                className="replay-row"
                data-testid={`replay-row-${id}`}
                role="button"
                tabIndex={0}
                onClick={() => onReplaySelect(replay)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onReplaySelect(replay);
                  }
                }}
              >
                <span className="replay-row-map">{replay.map_name}</span>
                {assetBadges}
                {renderFingerprints(replay)}
                <span title={formatAbsoluteDate(replay.replay_date)}>
                  {formatRelativeDate(replay.replay_date)}
                </span>
                <span>{formatDuration(replay.game_length)}</span>
                <span>{replay.format}</span>
                <span className="winner">{replay.winning_player}</span>
                <span>{replay.game_type}</span>
                <span className="replay-id">#{id}</span>
              </div>
            );
          }

          return (
            <div
              key={id}
              className="replay-card"
              onClick={() => onReplaySelect(replay)}
            >
              <div className="replay-header">
                <h3>{replay.map_name}</h3>
                <span className="replay-id">#{id}</span>
              </div>

              {assetBadges}

              {renderFingerprints(replay)}

              <div className="replay-info">
                <div className="info-row">
                  <span className="label">Date:</span>
                  <span title={formatAbsoluteDate(replay.replay_date)}>
                    {formatRelativeDate(replay.replay_date)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="label">Duration:</span>
                  <span>{formatDuration(replay.game_length)}</span>
                </div>
                <div className="info-row">
                  <span className="label">Format:</span>
                  <span>{replay.format}</span>
                </div>
                <div className="info-row">
                  <span className="label">Winner:</span>
                  <span className="winner">{replay.winning_player}</span>
                </div>
                <div className="info-row">
                  <span className="label">Type:</span>
                  <span>{replay.game_type}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {renderPagination('bottom')}
    </div>
  );
};

export default ReplaysList;

